import { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { createManifest, getPopupSearchParams } from './utils';
import { webStorageUtil } from 'state/webStorage.state.service';
import { ETheme, themes } from './themes/theme';
import { BootResponse } from './constants/apiResponses.types';
import {
  EFeatureFlag,
  ELocalStorageKeys,
  EQueryParams,
  ESessionStorageKeys,
  EStorageType,
} from './constants/enums';
import useApi from './hooks/useApi';
import useAnalytics from './hooks/useAnalytics';
import useSetCssVars from './hooks/useSetCssVars';
import useThemeFont from './hooks/useThemeFont';
import { EventBatchProvider } from './components/EventsBatchProvider/EventBatchProvider';
import DevDialog from './components/DevDialog/DevDialog';
import { v4 as uuidv4 } from 'uuid';
import { changeLanguage, initI18n } from './i18n';
import './styles.scss';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { Loader } from './components/Loader/Loader';

const LoginTemplate = lazy(() =>
  import('./pages/login/LoginTemplate').then((m) => ({ default: m.LoginTemplate }))
);
const ShopPage = lazy(() =>
  import('pages/Shop/ShopPage').then((m) => ({ default: m.ShopPage }))
);
const Maintenance = lazy(() =>
  import('./pages/maintenance/maintenance').then((m) => ({ default: m.Maintenance }))
);
const Failed = lazy(() =>
  import('./pages/failed/failed').then((m) => ({ default: m.Failed }))
);
const Support = lazy(() => import('./pages/Support/Support'));
import { useAppState } from './state/hooks/app.state.hook';
import { useLocalizationState } from './state/hooks/localization.state.hook';
import MaintenancePage from 'components/MaintenancePage/MaintenancePage';
import useUrlHash from 'hooks/useUrlHash';
import useUrlQuery from 'hooks/useUrlQuery';
import { DEFAULT_SYSTEM_LANGUAGE } from 'constants/defaults';
import { Footer } from 'components/footer/footer';
import { Background, getEnvVariable } from '@appcharge/shared-ui';
import { InfoModal } from 'components/InfoModal/InfoModal';
import { RotateDevicePromptWrapper } from 'components/RotateDevicePromptWrapper/RotateDevicePromptWrapper';
import useIndexedDB from './hooks/useIndexedDB';
import { DEFAULT_TTL } from 'state/hooks/login.state.hook';
import { StoreTemplateErrorHandler } from 'components/StoreTemplateErrorHandler/StoreTemplateErrorHandler';
import useCoralogixMonitoring from './hooks/useCoralogixMonitoring';
import useAccessibility from './hooks/useAccessibility';
import { healthMonitor } from './utils/healthMonitoring';
import { initCoralogixRum } from 'utils/coralogix';
import { isProdEnv } from 'utils/env';
import { useMaintenanceMode } from 'hooks/useMaintenanceMode';
import { getStoreSettings } from 'utils/storeSettings';

const theme = createTheme(themes[ETheme.LIGHT]);

const TABS_FOOTER_HEIGHT = '78px';

type Environment = 'sandbox' | 'dev' | 'prod' | 'production' | undefined;
const TEST_INACTIVITY_LIMIT = 60 * 60 * 1000;
const CHECK_INTERVAL = 60 * 1000;
const SHARED_UI_VER = process.env.REACT_APP_SHARED_UI_VERSION;
const SDK_VER = process.env.REACT_APP_CHECKOUT_SDK_VERSION;
const ENV = getEnvVariable('REACT_APP_ENV', process.env.REACT_APP_ENV) as Environment;

console.log('Environment: ', ENV);

console.log('SUIV: ' + SHARED_UI_VER);
console.log('ICSV: ' + SDK_VER);

function App() {
  const API = useApi({});
  const publisherMetaData = API.getPublisherMeta.data as BootResponse;
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const [themeLoaded, setThemeLoaded] = useState(false);
  const { isI18nReady } = useLocalizationState();
  const [showDevDialog, setShowDevDialog] = useState(false);
  const [isCoralogixInitialized, setIsCoralogixInitialized] = useState(false);
  const {
    backgroundProps,
    displayLoader,
    isReconnecting,
    setDisplayLoader,
    infoModalUrl,
    isTabFooterDisplayed,
    setInstallPrompt,
  } = useAppState();
  const facebookAccessToken = useUrlHash('#access_token');
  const isAppleRedirect = useUrlQuery('apple') === 'true';
  // const loginData = useUrlQuery('forceLoginData');
  const { getAllValues } = useIndexedDB(['sessions']);
  const { data: dbSessionData, isLoading: isDBSessionDataLoading } = getAllValues('sessions');
  const isUserLoggedIn = webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN) !== null;
  const { sendHealthLog } = useCoralogixMonitoring();

  const isStandalone = window.location.search.includes('mode=standalone');
  const supportMultiLanguageEnabled = publisherMetaData?.featureFlags.store_multi_language_support;
  const currentLanguage = webStorageUtil.get(ELocalStorageKeys.CURRENT_LANGUAGE);
  const resolvedSettings = publisherMetaData
    ? getStoreSettings(publisherMetaData.storeTheme)
    : undefined;
  const loaderAnimationUrl = resolvedSettings?.loaderAnimation;
  const location = useLocation();

  useAccessibility({
    publisherId: publisherMetaData?.storeTheme?.publisherId,
    enabled: publisherMetaData?.featureFlags?.[EFeatureFlag.STORE_ACCESSIBILITY_SCRIPT_SUPPORT],
  });

  if (API.getPublisherMeta.isIdle) {
    API.getPublisherMeta.refetch();
  }

  // Log application startup health
  useEffect(() => {
    sendHealthLog('startup');
    // Start the comprehensive health monitor
    healthMonitor.startPeriodicHealthChecks(5); // 5 minutes interval
  }, [sendHealthLog]);

  const updateViewport = () => {
    const viewportMeta = document.querySelector('meta[name="viewport"]');

    if (viewportMeta) {
      if (publisherMetaData?.featureFlags?.[EFeatureFlag.STORE_DISABLE_VIEWPORT_SCALE]) {
        viewportMeta.setAttribute(
          'content',
          'width=device-width, height=device-height, viewport-fit=cover, user-scalable=no'
        );
      } else {
        viewportMeta.setAttribute(
          'content',
          'initial-scale=0.9, width=device-width, height=device-height, viewport-fit=cover, user-scalable=no'
        );
      }
    }
  };

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      console.log('beforeinstallprompt event triggered');
      setInstallPrompt(e);
      webStorageUtil.set(ESessionStorageKeys.INSTALL_PROMPT_FIRED, 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('source') === 'webview' ? 'webview' : '';
    webStorageUtil.set(ESessionStorageKeys.SOURCE, source);
  }, []);

  const useDynamoJwtFF =
    publisherMetaData?.featureFlags[EFeatureFlag.CHECKOUT_LINKS_USE_DYNAMO_JWT];

  const checkAutoCollectParam = () => {
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href);
      return currentUrl.searchParams.get(EQueryParams.AUTO_COLLECT) === 'true';
    }
    return false;
  };

  const hasAutoCollectParam = checkAutoCollectParam();

  useEffect(() => {
    const isInLoginProcess = Boolean(facebookAccessToken || isAppleRedirect);

    const isInCampaignLoginProcess = Boolean(
      !location.search.includes('from=campaign') &&
        useDynamoJwtFF &&
        (location.search.includes(EQueryParams.JWT_PARAM) ||
          location.search.includes(EQueryParams.D10X_PAYLOAD))
    );

    const hasAutoCollect = checkAutoCollectParam();

    const isWaitingForStoreTemplate =
      isUserLoggedIn &&
      Boolean(dbSessionData && dbSessionData.length > 0) &&
      !API.getStoreTemplate.isSuccess;

    const isLoadingApp =
      !isI18nReady ||
      !publisherMetaData ||
      isInLoginProcess ||
      isInCampaignLoginProcess ||
      hasAutoCollect ||
      isWaitingForStoreTemplate;

    setDisplayLoader(isLoadingApp);
    if (publisherMetaData && isProdEnv() && !isCoralogixInitialized) {
      initCoralogixRum(publisherMetaData);
      setIsCoralogixInitialized(true);
    }
  }, [
    isI18nReady,
    publisherMetaData,
    isCoralogixInitialized,
    hasAutoCollectParam,
    isUserLoggedIn,
    dbSessionData,
    API.getStoreTemplate.isSuccess,
    location.search,
  ]);

  useEffect(() => {
    if (isStandalone) {
      webStorageUtil.set(ESessionStorageKeys.SOURCE, 'home screen bookmark');
    }
  }, [isStandalone]);

  const generateNewSession = () => {
    const newSessionId = uuidv4();
    const newTtl = Date.now() + DEFAULT_TTL;
    const sessionValue = {
      id: newSessionId,
      ttl: newTtl,
    };
    webStorageUtil.set(
      ESessionStorageKeys.SESSION_DATA,
      sessionValue,
      EStorageType.SESSION_STORAGE
    );
    webStorageUtil.set(ELocalStorageKeys.SESSION_DATA, sessionValue, EStorageType.LOCAL_STORAGE);
  };

  const setupSessionInactivityHandler = () => {
    let lastActivityTime = Date.now();

    const activityHandler = () => {
      lastActivityTime = Date.now();
    };

    window.addEventListener('mousemove', activityHandler);
    window.addEventListener('keydown', activityHandler);
    window.addEventListener('scroll', activityHandler);

    const interval = setInterval(() => {
      const now = Date.now();
      if (now - lastActivityTime > TEST_INACTIVITY_LIMIT) {
        generateNewSession();
        lastActivityTime = now;
      }
    }, CHECK_INTERVAL);

    return () => {
      window.removeEventListener('mousemove', activityHandler);
      window.removeEventListener('keydown', activityHandler);
      window.removeEventListener('scroll', activityHandler);
      clearInterval(interval);
    };
  };

  // todo: add to GP somehow
  useEffect(() => {
    const sessionData = webStorageUtil.getStorageWithFallback();
    const params = new URLSearchParams(window.location.search);
    const sessionIdParam = params.get('sessionId');
    if (!sessionData || Date.now() > sessionData.ttl) {
      generateNewSession();
    }
    if (sessionIdParam) {
      params.delete('sessionId');
      navigate(`${window.location.pathname}?${params.toString()}`, {
        replace: true,
      });
    }
    const cleanup = setupSessionInactivityHandler();
    return cleanup;
  }, []);

  useEffect(() => {
    if (publisherMetaData && supportMultiLanguageEnabled) {
      const updateI18nConfig = async () => {
        const publisherId = publisherMetaData?.storeTheme?.publisherId;
        if (!publisherId) {
          console.warn('PublisherId not available yet, skipping i18n initialization');
          return;
        }

        await initI18n({
          getLanguagesList: API.getLanguagesList,
          publisherId,
          language: publisherMetaData.language,
        });
      };
      updateI18nConfig();
    }
  }, [publisherMetaData?.storeTheme?.publisherId, supportMultiLanguageEnabled]);

  useEffect(() => {
    if (publisherMetaData && !supportMultiLanguageEnabled && !isI18nReady) {
      const initializeBasicI18n = async () => {
        try {
          await initI18n({
            getLanguagesList: API.getLanguagesList,
            publisherId: publisherMetaData?.storeTheme?.publisherId || 'default',
            language: publisherMetaData.language || 'en',
          });
        } catch (error) {
          console.warn('Failed to initialize basic i18n, using fallback:', error);
        }
      };
      initializeBasicI18n();
    }
  }, [publisherMetaData, supportMultiLanguageEnabled, isI18nReady]);

  useEffect(() => {
    if (
      publisherMetaData?.featureFlags &&
      !supportMultiLanguageEnabled &&
      currentLanguage !== DEFAULT_SYSTEM_LANGUAGE
    ) {
      changeLanguage('en');
    }
  }, [publisherMetaData?.featureFlags, currentLanguage, supportMultiLanguageEnabled]);

  useEffect(() => {
    API.getPublisherMeta.refetch();
  }, [currentLanguage]);

  // Set global CSS vars for theme
  useSetCssVars();
  useThemeFont();

  const setFavicon = () => {
    if (publisherMetaData && resolvedSettings) {
      const faviconUrl = resolvedSettings.favicon;
      const appIcon = publisherMetaData.storeTheme.storeScreen.addToHomeScreen?.active
        ? publisherMetaData.storeTheme.storeScreen.addToHomeScreen?.iconImage
        : faviconUrl;

      // Update main favicon
      const fav = document.getElementById('favicon');
      if (fav) {
        fav.setAttribute('href', faviconUrl);
      }

      // Update PNG favicons
      const favicon32 = document.querySelector('link[rel="icon"][sizes="32x32"]');
      if (favicon32) {
        favicon32.setAttribute('href', faviconUrl);
      }

      const favicon16 = document.querySelector('link[rel="icon"][sizes="16x16"]');
      if (favicon16) {
        favicon16.setAttribute('href', faviconUrl);
      }

      // Update Apple Touch Icon
      const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
      if (appleTouchIcon) {
        appleTouchIcon.setAttribute('href', appIcon);
      }
    }
  };

  const setThemeColor = () => {
    if (!resolvedSettings) return;
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', resolvedSettings.themeColor);
    }
    document.documentElement.style.setProperty(
      '--app-background-color',
      resolvedSettings.themeColor,
    );
  };

  const setOverscrollBehavior = () => {
    const root = document.getElementById('root');
    if (root) {
      root.style.overscrollBehavior = resolvedSettings?.bouncyScrollOn ? 'contain' : 'none';
    }
  };

  useEffect(() => {
    if (API.getPublisherMeta.isSuccess && publisherMetaData && resolvedSettings && !themeLoaded) {
      window.document.title = resolvedSettings.storeTabName;
      analytics.init();
      setFavicon();
      setThemeColor();
      setOverscrollBehavior();
      createManifest(publisherMetaData);
      updateViewport();
      // Log theme loaded health
      sendHealthLog('periodic');

      setThemeLoaded(true);
    }
  }, [API.getPublisherMeta.isSuccess, publisherMetaData, themeLoaded, analytics]);

  useEffect(() => {
    if (dbSessionData && dbSessionData.length > 0 && isUserLoggedIn) {
      API.getStoreTemplate.refetch();
    }
  }, [dbSessionData, isUserLoggedIn]);

  const maintenanceState = useMaintenanceMode();

  const loginRedirectSearch = location.search.includes('campaign')
    ? location.search
    : getPopupSearchParams(location.search);

  if (maintenanceState.isActive) {
    return <MaintenancePage />;
  }

  return (
    <ErrorBoundary>
      <EventBatchProvider>
        <ThemeProvider theme={theme}>
          <RotateDevicePromptWrapper publisherMetaData={publisherMetaData} />
          {(displayLoader || isReconnecting || hasAutoCollectParam) && (
            <Loader loaderAnimationUrl={loaderAnimationUrl} />
          )}
          <div
            style={{
              background: 'transparent',
              width: 30,
              height: 30,
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 100000,
              overflowX: 'hidden',
            }}
            onClick={(e) => {
              if (
                (window.location.hostname.includes('.appcharge.com') ||
                  window.location.hostname.includes('.appchargestore.com') ||
                  window.location.hostname.includes('.appcharge-int.com') ||
                  window.location.hostname.includes('localhost')) &&
                e.detail === 3
              ) {
                setShowDevDialog(true);
              }
            }}
          />
          {showDevDialog && <DevDialog onClose={() => setShowDevDialog(false)} />}
          {window.location.hostname.includes('.appcharge.com') &&
            (webStorageUtil.get(ELocalStorageKeys.OFFER_API_OVERRIDE) ||
              webStorageUtil.get(ELocalStorageKeys.BOOT_API_OVERRIDE) ||
              webStorageUtil.get(ELocalStorageKeys.POST_LOGIN_API_OVERRIDE)) && (
              <div
                style={{
                  position: 'fixed',
                  zIndex: 99,
                  background: 'red',
                  width: '100vw',
                  padding: '0.5em',
                  textAlign: 'center',
                  color: '#fff',
                }}
              >
                {'SOME ROUTES MOCKED'}
              </div>
            )}
          {themeLoaded && !isDBSessionDataLoading && (
            <Background isStore {...backgroundProps} style={{ paddingTop: '60px' }}>
              <StoreTemplateErrorHandler />
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/login/*" element={<LoginTemplate />} />
                  <Route
                    path="/shop"
                    element={
                      !isUserLoggedIn || !dbSessionData || dbSessionData.length === 0 ? (
                        <Navigate
                          to={`/login${loginRedirectSearch}`}
                          replace
                        />
                      ) : API.getStoreTemplate.isSuccess ? (
                        <ShopPage />
                      ) : null
                    }
                  />
                  <Route path="/maintenance" element={<Maintenance />} />
                  <Route path="/failed" element={<Failed />} />
                  <Route path="/support/:supportType/:storePhase/:orderId?" element={<Support />} />
                  <Route path="*" element={<Navigate to="/shop" replace />} />
                </Routes>
              </Suspense>
              {infoModalUrl && <InfoModal />}
              <Footer />
              {isTabFooterDisplayed && <div aria-hidden style={{ height: TABS_FOOTER_HEIGHT }} />}
            </Background>
          )}
        </ThemeProvider>
      </EventBatchProvider>
    </ErrorBoundary>
  );
}

export default App;
